// The count on the Support nav item, and the pop-up when something new arrives.
//
// Rendered only where the Support item itself is rendered, so a role that cannot
// open Support never runs these queries or opens the socket (the sidebar already
// filters its items by role).
//
// Two jobs, one subscription:
//   · keep the lists fresh — `support_messages` is in the realtime publication
//     (0170) and Realtime applies the same RLS as a read, so a hub coordinator is
//     only told about their own hub's messages. An insert just invalidates the
//     queries; the data still arrives through the normal, scoped reads.
//   · tell the person — a toast for any new message that is not their own reply
//     or the assistant's, unless they are already on the Support page where the
//     list shows it. A colleague's reply therefore counts too: telling it from a
//     client's ask would need the thread, and a spare toast costs less than a
//     missed one.
//
// ⚠ DATA WALL. The toast carries the message text and nothing else — no name,
// phone, email or address — the same words the thread shows that role anyway.
//
// The 30 s poll in useSupportTickets is the fallback if the socket drops, and
// the count recomputes from it either way.

import { Badge } from "@bednbody/ui/components/badge";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useOpenSosAlerts } from "@/features/sos/sos.hooks";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { useSeenThreads, useSupportTickets } from "./support.hooks";
import { toInboxItems, unreadOpenCount } from "./support.model";

interface InsertedMessage {
  sender: "client" | "ai" | "staff";
  sender_id: string | null;
  body: string;
}

export function SupportNavBadge({ pathname }: { pathname: string }) {
  const { session } = useAuth();
  const myId = session?.user.id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tickets = useSupportTickets();
  const sos = useOpenSosAlerts();
  const { seen } = useSeenThreads(myId);

  // The subscription must not be torn down every time the route changes.
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    const channel = supabase
      .channel("support-inbox")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "support_messages" },
        (payload) => {
          void queryClient.invalidateQueries({ queryKey: ["support"] });
          const message = payload.new as InsertedMessage;
          if (message.sender === "ai" || message.sender_id === myId) return;
          if (pathRef.current.startsWith("/support")) return;
          toast("New support message", {
            description: message.body.length > 90 ? `${message.body.slice(0, 90)}…` : message.body,
            action: { label: "Open", onClick: () => navigate("/support") },
          });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient, navigate, myId]);

  const unread = useMemo(
    () =>
      unreadOpenCount(
        toInboxItems(
          sos.data ?? [],
          tickets.data?.tickets ?? [],
          tickets.data?.bookingRefById ?? new Map(),
          tickets.data?.lastMessageByThread,
        ),
        seen,
      ),
    [sos.data, tickets.data, seen],
  );

  if (unread === 0) return null;
  return (
    <Badge aria-label={`${unread} unread`} className="ml-auto" variant="destructive">
      {unread > 99 ? "99+" : unread}
    </Badge>
  );
}
